import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ReferencesDrawer } from "./index";

export interface Reference {
  id?: string;
  dim_id?: number;
  person_id?: number;
  file_id?: number;
  type?: string;
  label?: string;
  onClose?: () => void;
}

interface ReferencesContextType {
  showReferences: (refs: Reference[], data: any) => void;
  closeReferences: () => void;
}

export const useReferences = () => {
  const context = React.useContext(ReferencesContext);
  if (!context) {
    throw new Error("useReferences must be used within a ReferencesProvider");
  }
  return context;
};

const ReferencesContext = createContext<ReferencesContextType | undefined>(
  undefined
);

export const ReferencesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const functionsRef = useRef<{
    showReferences: (references: Reference[], data: any) => void;
    closeReferences: () => void;
  } | null>(null);

  // State to track if references drawer is shown
  const [isShown, setIsShown] = useState(false);
  const [references, setReferences] = useState<Reference[]>([]);
  const [data, setData] = useState<any>(null);

  // Show references drawer
  const showReferences = useCallback((refs: Reference[], data: any) => {
    setReferences(refs);
    setData(data);
    setIsShown(true);
  }, []);

  // Close references drawer
  const closeReferences = useCallback(() => {
    setReferences([]);
    setData(null);
    setIsShown(false);
  }, []);

  // Store functions in ref to avoid circular dependency
  useEffect(() => {
    functionsRef.current = { showReferences, closeReferences };
  }, [showReferences, closeReferences]);

  // Listen for show-toast events
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent;
      const _references = customEvent.detail?.references;
      const _data = customEvent?.detail?.data;
      showReferences(_references, _data);
    };

    const closeHandler = (e: Event) => {
      const customEvent = e as CustomEvent;
      const id = customEvent.detail?.id;
      if (id) {
        closeReferences();
      } else {
        // Close all toasts if no specific ID provided
        // setReferencess([]);
      }
    };

    window.addEventListener("show-references", handler as EventListener);
    window.addEventListener("close-references", closeHandler as EventListener);
    return () => {
      window.removeEventListener("show-references", handler as EventListener);
      window.removeEventListener(
        "close-references",
        closeHandler as EventListener
      );
    };
  }, [showReferences, closeReferences]);

  return (
    <ReferencesContext.Provider value={{ showReferences, closeReferences }}>
      {children}
      {/* References drawer container */}
      <AnimatePresence>
        {isShown && (
          <motion.div
            className="fixed inset-0 z-40 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeReferences}
          />
        )}
        {isShown && (
          <motion.div
            className="absolute bottom-0 right-0 z-50 h-full w-full md:w-[500px]"
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <ReferencesDrawer
              references={references}
              data={data}
              onClose={closeReferences}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </ReferencesContext.Provider>
  );
};
